import React, { useState, useMemo, useCallback } from 'react';
import Modal from 'pages/components/Modal/Modal';
import CustomButton from 'pages/components/CustomButton/CustomButton';
import Certifications from './Certifications/Certifications';
import BVLOSOperations from './BVLOSOperations/BVLOSOperations';
import PackageParts from './PackageParts/PackageParts';
import DownloadComponent from './pages/components/DownloadComponent';
import WhereToBuy from './WhereToBuy/WhereToBuy';
import HeroSection from './pages/components/HeroSection';
import { useApi } from 'hooks/useApi';
import styles from './Axl30.module.css';

const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

const Axl30Drone = () => {
  const [isWhereToBuyModalOpen, setIsWhereToBuyModalOpen] = useState(false);

  const { data: productData, loading: productLoading, error: productError } = useApi('/product-types', { params: { slug: 'axl-30' } });
  const { data: specificationsResponse, loading: specsLoading, error: specsError } = useApi('/products', { params: { slug: 'axl-30' } });

  const specificationsData = useMemo(() => {
    if (!specificationsResponse?.success || !specificationsResponse?.products) return [];
    return specificationsResponse.products.map(product => ({
      ...product,
      partsList: product.specification?.map(spec => spec.name) || [],
      specifications: product.specification?.map(spec => ({ label: spec.name, value: spec.value })) || []
    }));
  }, [specificationsResponse]);

  const getFeaturesData = useMemo(() => {
    if (!productData?.success || !productData?.products?.features) return [];
    return productData.products.features.map(item => ({
      icon: item.icon,
      label: item.title,
      image: item.image || 'Assets/Images/Axl30/bvlos_operation.svg'
    }));
  }, [productData]);

  const getUseCasesData = useMemo(() => {
    if (!productData?.success || !productData?.products?.designed_for) return [];
    return productData.products.designed_for.map(item => ({
      icon: item.icon,
      label: item.title,
      image: item.image || 'Assets/Images/Axl30/Suitabefor.svg'
    }));
  }, [productData]);

  const getStoreData = useMemo(() => {
    if (!productData?.success || !productData?.products?.store) return [];
    return productData.products.store;
  }, [productData]);

  const product = useMemo(() => productData?.success ? productData.products : null, [productData]);

  const handleOpenWhereToBuyModal = useCallback(() => setIsWhereToBuyModalOpen(true), []);
  const handleCloseWhereToBuyModal = useCallback(() => setIsWhereToBuyModalOpen(false), []);

  const loading = productLoading || specsLoading;
  const error = productError || specsError;

  if (loading) return (
    <div className={styles.pageWrapper}>
      <div className={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className={styles.pageWrapper}>
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <HeroSection
          subtitle={product?.title || "axl30"}
          title={product?.banner_heading || "Designed for Adventurers. Built by You"}
          gradientFrom="#F97738"
          gradientTo="#FCB999"
          showBottomFade={false}
        />
      </section>

      {/* Drone Overlap Section */}
      <div className={styles.droneWrapper}>
        <img
          id="droneImage"
          src={product?.banner_image ? `${CMS_BASE_URL}/attachments/${product.banner_image}` : 'Assets/Images/Axl30/Axl30Drone.svg'}
          alt="axl30 Drone Kit"
          className={styles.droneImage}
          width="600"
          height="450"
          loading="eager"
          fetchpriority="high"
        />
      </div>

      {/* Main Content */}
      <div className={styles.mainContainer}>
        {/* Description */}
        <section className={styles.descriptionSection}>
          <h2>
            {product?.description ||
              "axl30 Drone Kit is a Carbon Fiber FPV Quadcopter Airframe that provides durability and lightweight performance for agile flight. This promises an exhilarating journey into the world of drone assembly. Package Includes all essential components for assembly and each component is meticulously designed for optimal performance and longevity. As you embark on this thrilling endeavor, the axl30 Drone Kit is not just a collection of parts; its an invitation to defy gravity, to soar beyond limits, and to carve your path through the boundless skies of imagination."
            }
          </h2>
        </section>

        {/* Where to Buy Button */}
        {getStoreData.length > 0 && (
          <section className={styles.whereToBuySection}>
            <CustomButton
              onClick={handleOpenWhereToBuyModal}
              size="large"
              ariaLabel="Where to Buy"
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 2.5M7 13l2.5 2.5m6 2a2 2 0 100-4 2 2 0 000 4zm-6 0a2 2 0 100-4 2 2 0 000 4z" /></svg>}
            >
              Where to Buy
            </CustomButton>
          </section>
        )}

        {/* Where to Buy Modal */}
        <Modal isOpen={isWhereToBuyModalOpen} onClose={handleCloseWhereToBuyModal} title="Where to Buy" maxWidth="600px">
          <WhereToBuy storeData={getStoreData} />
        </Modal>

        {/* Full Width Sections */}
        {getFeaturesData.length > 0 && (
          <BVLOSOperations title="Features" operations={getFeaturesData} />
        )}

        {getUseCasesData.length > 0 && (
          <BVLOSOperations 
            title={product?.designed_for_title || "Suitable for"} 
            operations={getUseCasesData} 
          />
        )}

        <Certifications />

        {/* Package Parts and Downloads */}
        <div className={styles.specificationWrapper}>
          {specificationsData.map((specProduct, index) => (
            <React.Fragment key={specProduct.slug || index}>
              <PackageParts
                title={specProduct.title || ""}
                description={specProduct.description || ""}
                partsList={specProduct.partsList || []}
              />
              
              <DownloadComponent
                brochure={specProduct.brochure ? `${CMS_BASE_URL}/attachments/${specProduct.brochure}` : null}
                userGuide={specProduct.user_guide ? `${CMS_BASE_URL}/attachments/${specProduct.user_guide}` : null}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Axl30Drone;